// BV-1586: giá sau ưu đãi (deal + chiết khấu thanh toán + voucher) ở PDP/PLP và giá đã giảm theo dòng
// trong giỏ. File này chỉ chứa hàm thuần (không import), dùng chung cho WebService, PriceBreakdown và giỏ.
//
// Dữ liệu từ BE (kh_frontend-apis, port từ TH, contract docs/BV-1586 mục 5.1, 5.2):
//   consumedPrice.priceAfterDiscount, consumedPrice.totalDiscount, product.discountPriceInfo[]
//   discountPriceInfo[] = { key, index, discount, ...field theo key }
// Thiếu field (BE chưa deploy, chưa đăng nhập, lỗi engine) -> isShowPriceAfterDiscount = false, hiển thị như cũ.
//
// KH (quyết định K1): tiền là riel (៛), không có số lẻ. formatCurrency của web không làm tròn nên mọi số
// tiền đều làm tròn tới riel nguyên (roundMoney) trước khi format.

export const DISCOUNT_PRICE_KEY = {
  DEAL: 'DEAL',
  BANK_DISCOUNT: 'BANK_DISCOUNT',
  VOUCHER_PLATFORM: 'VOUCHER_PLATFORM',
  VOUCHER_SELLER: 'VOUCHER_SELLER',
};

export const PRICE_BREAKDOWN_ROW = {
  ORIGIN_PRICE: 'ORIGIN_PRICE',
  DEAL: DISCOUNT_PRICE_KEY.DEAL,
  PRICE_AFTER_DEAL: 'PRICE_AFTER_DEAL',
  BANK_DISCOUNT: DISCOUNT_PRICE_KEY.BANK_DISCOUNT,
  VOUCHER_PLATFORM: DISCOUNT_PRICE_KEY.VOUCHER_PLATFORM,
  VOUCHER_SELLER: DISCOUNT_PRICE_KEY.VOUCHER_SELLER,
  FINAL_PRICE: 'FINAL_PRICE',
};

const KNOWN_KEYS = Object.values(DISCOUNT_PRICE_KEY);

// null cho giá trị rỗng / không phải số, để phân biệt "không có" với 0
export const toPriceNumber = (value) => {
  if (value === null || value === undefined || value === '' || typeof value === 'boolean')
    return null;
  const num = Number(value);
  return Number.isFinite(num) ? num : null;
};

// Số tiền riel: làm tròn tới riel nguyên (null giữ null). Dùng trước mọi lần format tiền.
export const roundMoney = (value) => {
  const num = toPriceNumber(value);
  return num === null ? null : Math.round(num);
};

const round2 = (value) => Math.round((value + Number.EPSILON) * 100) / 100;

// Giữ dòng có key đã biết và discount > 0, sort theo index (thiếu index thì xếp cuối, giữ thứ tự BE)
export const normalizeDiscountPriceInfo = (list) => {
  if (!Array.isArray(list)) return [];
  return list
    .map((line, position) => ({ line, position }))
    .filter(
      ({ line }) => line && KNOWN_KEYS.indexOf(line.key) >= 0 && toPriceNumber(line.discount) > 0,
    )
    .sort((a, b) => {
      const indexA = toPriceNumber(a.line.index) ?? Number.MAX_SAFE_INTEGER;
      const indexB = toPriceNumber(b.line.index) ?? Number.MAX_SAFE_INTEGER;
      return indexA - indexB || a.position - b.position;
    })
    .map(({ line }) => ({ ...line, discount: toPriceNumber(line.discount) }));
};

// Contract mục 6.2: chỉ hiển thị khi có priceAfterDiscount và có ít nhất 1 dòng khác DEAL với discount > 0
export const hasPriceAfterDiscount = ({ priceAfterDiscount, discountPriceInfo } = {}) => {
  const finalPrice = toPriceNumber(priceAfterDiscount);
  if (finalPrice === null || finalPrice < 0) return false;
  return normalizeDiscountPriceInfo(discountPriceInfo).some(
    (line) => line.key !== DISCOUNT_PRICE_KEY.DEAL,
  );
};

// Map field BE vào product của web (WebService.convertProductV2). Không ghi đè field giá cũ.
// consumedPrice.totalDiscount không map: UI không dùng, và tên totalDiscount đã có nghĩa khác ở order.
export const mapPriceAfterDiscount = ({
  consumedPrice,
  discountPriceInfo,
  priceAfterDeal,
  retailPriceValue,
  withoutLogin = false,
} = {}) => {
  const lines = normalizeDiscountPriceInfo(discountPriceInfo);
  const priceAfterDiscount = toPriceNumber(consumedPrice?.priceAfterDiscount);
  const originPrice =
    toPriceNumber(consumedPrice?.retailPriceValue) ?? toPriceNumber(retailPriceValue);
  const dealLine = lines.find((line) => line.key === DISCOUNT_PRICE_KEY.DEAL);

  // giá sau deal/campaign lấy từ BE (P = consumedPrice.currentPrice), chỉ tự suy ra khi BE thiếu
  let priceAfterDealValue =
    toPriceNumber(consumedPrice?.priceAfterDeal) ??
    toPriceNumber(priceAfterDeal) ??
    toPriceNumber(consumedPrice?.currentPrice);
  if (priceAfterDealValue === null && originPrice !== null) {
    priceAfterDealValue = dealLine ? round2(originPrice - dealLine.discount) : originPrice;
  }

  const isShowPriceAfterDiscount =
    !withoutLogin &&
    originPrice !== null &&
    hasPriceAfterDiscount({ priceAfterDiscount, discountPriceInfo: lines });

  return {
    discountPriceInfo: lines,
    priceAfterDiscount,
    priceAfterDeal: priceAfterDealValue,
    originPrice,
    isShowPriceAfterDiscount,
  };
};

// Số % hiển thị: tối đa 2 chữ số thập phân, bỏ số 0 thừa (0.5 -> "0.5", 5 -> "5", 0.31428 -> "0.31")
export const formatPercentNumber = (value) => {
  const num = toPriceNumber(value);
  if (num === null) return '';
  return String(round2(num));
};

// rate của VOUCHER_PLATFORM là tỉ lệ (0.0031428571) -> "0.31"
// rate > 0 nhưng làm tròn 2 chữ số ra 0 (vd 0.00002 -> 0.002%) -> "<0.01", không hiện "0%"
export const MIN_RATE_PERCENT_LABEL = '<0.01';
export const formatRatePercent = (rate) => {
  const num = toPriceNumber(rate);
  if (num === null || num <= 0) return '';
  const percent = num * 100;
  return round2(percent) > 0 ? formatPercentNumber(percent) : MIN_RATE_PERCENT_LABEL;
};

// Mặc định giống formatCurrency của web KH (env cam): "12,300 ៛" (dấu phân cách ",", ៛ đặt sau số)
const RIEL_SYMBOL = '៛';
const defaultFormatRiel = (value) =>
  `${String(value).replace(/\B(?=(\d{3})+(?!\d))/g, ',')} ${RIEL_SYMBOL}`;

// Bọc formatter: làm tròn tới riel nguyên trước khi format, null / không phải số -> ''
const withRoundMoney = (format) => (value) => {
  const amount = roundMoney(value);
  return amount === null ? '' : format(amount);
};

const buildBankRow = (line, formatDiscount) => {
  const percent = formatPercentNumber(line.percent);
  return {
    id: PRICE_BREAKDOWN_ROW.BANK_DISCOUNT,
    // tiêu đề là tên phương thức thanh toán (pricing-v2), thiếu thì dùng nhãn chung
    label: line.paymentMethodName || '',
    labelKey: line.paymentMethodName ? '' : 'priceBreakdown.bankDiscount',
    subKey: percent ? 'priceBreakdown.bankDiscountSub' : '',
    subParams: { percent },
    subText: '',
    value: formatDiscount(line.discount),
    isDiscount: true,
    voucherCode: '',
  };
};

const buildPlatformRow = (line, formatDiscount, formatAmount) => {
  const rate = formatRatePercent(line.rate);
  const minOrderValue = toPriceNumber(line.minOrderValue);
  let subKey = '';
  if (rate && minOrderValue > 0) subKey = 'priceBreakdown.platformVoucherSub';
  else if (rate) subKey = 'priceBreakdown.platformVoucherRateSub';
  return {
    id: PRICE_BREAKDOWN_ROW.VOUCHER_PLATFORM,
    label: '',
    labelKey: 'priceBreakdown.platformVoucher',
    subKey,
    // KH: minOrderValue là số tiền riel đã format kèm ៛ ("50,000 ៛"), text i18n không ghi đơn vị tiền
    subParams: { rate, minOrderValue: minOrderValue > 0 ? formatAmount(minOrderValue) : '' },
    subText: subKey ? '' : line.displayName || '',
    value: formatDiscount(line.discount),
    isDiscount: true,
    voucherCode: line.voucherCode || '',
  };
};

const buildSellerRow = (line, formatDiscount) => ({
  id: PRICE_BREAKDOWN_ROW.VOUCHER_SELLER,
  label: '',
  labelKey: 'priceBreakdown.sellerVoucher',
  subKey: '',
  subParams: {},
  subText: line.displayName || '',
  value: formatDiscount(line.discount),
  isDiscount: true,
  voucherCode: line.voucherCode || '',
});

// Dựng các dòng của popover / bottom sheet "Chi tiết giá" (contract mục 6.3).
// Trả cấu trúc (key i18n + params + giá đã format) để popover và bottom sheet render giống nhau.
//   formatPrice: formatter tiền của web (formatCurrency, riel)
//   formatAmount: formatter cho MOV của voucher platform; KH dùng luôn formatter tiền riel
// Cả 2 formatter nhận số đã làm tròn tới riel nguyên.
export const buildPriceBreakdownRows = (
  data = {},
  { formatPrice: rawFormatPrice = defaultFormatRiel, formatAmount: rawFormatAmount } = {},
) => {
  const { originPrice, priceAfterDeal, priceAfterDiscount, discountPriceInfo } = data || {};
  if (!hasPriceAfterDiscount({ priceAfterDiscount, discountPriceInfo })) return [];

  const formatPrice = withRoundMoney(rawFormatPrice);
  const formatAmount = withRoundMoney(rawFormatAmount || rawFormatPrice);
  const lines = normalizeDiscountPriceInfo(discountPriceInfo);
  const formatDiscount = (value) => `-${formatPrice(Math.abs(value))}`;
  const origin = toPriceNumber(originPrice);

  const priceRows = [
    {
      id: PRICE_BREAKDOWN_ROW.ORIGIN_PRICE,
      labelKey: 'priceBreakdown.originPrice',
      value: formatPrice(origin),
    },
  ];

  const dealLine = lines.find((line) => line.key === DISCOUNT_PRICE_KEY.DEAL);
  if (dealLine) {
    const afterDeal =
      toPriceNumber(priceAfterDeal) ??
      (origin !== null ? round2(origin - dealLine.discount) : null);
    priceRows.push(
      {
        id: PRICE_BREAKDOWN_ROW.DEAL,
        labelKey: 'priceBreakdown.directDiscount',
        value: formatDiscount(dealLine.discount),
        isDiscount: true,
      },
      {
        id: PRICE_BREAKDOWN_ROW.PRICE_AFTER_DEAL,
        labelKey: 'priceBreakdown.priceAfterDirectDiscount',
        value: formatPrice(afterDeal),
      },
    );
  }

  const discountRows = lines
    .map((line) => {
      switch (line.key) {
        case DISCOUNT_PRICE_KEY.BANK_DISCOUNT:
          return buildBankRow(line, formatDiscount);
        case DISCOUNT_PRICE_KEY.VOUCHER_PLATFORM:
          return buildPlatformRow(line, formatDiscount, formatAmount);
        case DISCOUNT_PRICE_KEY.VOUCHER_SELLER:
          return buildSellerRow(line, formatDiscount);
        default:
          return null;
      }
    })
    .filter(Boolean);

  // divider sau nhóm giá (ngay dưới "Giá sau giảm trực tiếp", hoặc "Giá gốc" khi không có deal)
  priceRows[priceRows.length - 1] = {
    ...priceRows[priceRows.length - 1],
    hasDividerAfter: discountRows.length > 0,
  };

  return [
    ...priceRows,
    ...discountRows,
    {
      id: PRICE_BREAKDOWN_ROW.FINAL_PRICE,
      labelKey: 'priceBreakdown.finalPrice',
      noteKey: 'priceBreakdown.finalPriceNote',
      value: formatPrice(priceAfterDiscount),
      isTotal: true,
      hasDividerBefore: true,
    },
  ];
};

// Giỏ hàng (contract mục 5.4): order-service trả cartItems[].price (đơn giá sau deal/campaign/deal advanced)
// và priceAfterVoucher (đơn giá sau voucher phân bổ vào SKU). Trả null khi không đủ điều kiện -> hiển thị như cũ.
// Luôn đọc price của chính cart item, không dùng salePrice/displayPrice (giá catalog do CartService ghép vào).
// KH: trả giá đã làm tròn tới riel nguyên và so sánh trên số đã làm tròn, để không hiện 2 giá bằng nhau.
export const getCartItemPriceAfterVoucher = (item) => {
  if (!item || item.isGift || item.isSelected === false) return null;
  const price = roundMoney(item.price);
  const priceAfterVoucher = roundMoney(item.priceAfterVoucher);
  if (price === null || priceAfterVoucher === null) return null;
  if (price <= 0 || priceAfterVoucher < 0 || priceAfterVoucher >= price) return null;
  return { price, priceAfterVoucher };
};
